import type {
  SubscriptionV2Variable,
  SubscriptionDetailView,
  SubscriptionResponse,
  RawSQLMetricConfig,
} from "@/pages/subscriptions/services/types";
import {
  extractEntityVariable,
  extractMetricVariable,
  extractAggregationVariable,
  extractChangeTypeVariable,
  extractComparisonVariable,
  extractThresholdVariable,
  extractDateRangeVariable,
  extractScheduleVariable,
  extractDestinationConfig,
} from "./variableExtractors";
import {
  SubscriptionType,
  SubscriptionStatus,
  VariableType,
} from "@/pages/subscriptions/constants";

function generatePrompt(
  rule_type: SubscriptionType,
  variables: SubscriptionV2Variable[],
  _entity_type: string
): string {
  const isAlert = rule_type === SubscriptionType.ALERT;
  if (isAlert) {
    // Find the change_type variable to determine prompt structure
    const changeTypeVar = variables.find(v => v.type === VariableType.CHANGE_TYPE);
    const changeType = changeTypeVar?.value as string | undefined;

    if (changeType === "absolute_value") {
      // Absolute value: no time_period comparison
      return "Send me an alert for {{entities}} when the {{change_type}} of the {{aggregation}} of {{metric}} {{comparison}} {{threshold}}. Check the entities for these changes {{schedule}}";
    } else {
      // Relative value (default): includes time_period comparison
      return "Send me an alert for {{entities}} when the {{change_type}} of the {{aggregation}} of {{metric}} {{comparison}} {{threshold}} compared to the {{time_period}}. Check the entities for these changes {{schedule}}";
    }
  } else {
    return "Send me a report for {{entities}} of the {{aggregation}} of {{metric}} {{schedule}}";
  }
}

/**
 * Type guard to check if the metric_config is a legacy format (custom SQL alerts)
 */
function isRawSQLMetricConfig(
  metricConfig: SubscriptionResponse["metric_config"]
): metricConfig is RawSQLMetricConfig {
  return !("metric_name" in metricConfig) && "trigger_query" in metricConfig;
}

/**
 * Main reverse transformation function
 */
export function transformBackendToFrontend(
  backendData: SubscriptionResponse
): SubscriptionDetailView {
  // For legacy alerts (RawSQL), return minimal view with description
  if (isRawSQLMetricConfig(backendData.metric_config)) {
    const readOnly = backendData.read_only ?? true;
    const destination_config = extractDestinationConfig(backendData.delivery?.notifiers ?? []);
    return {
      id: String(backendData.id),
      name: backendData.name,
      rule_type: backendData.rule_type,
      platform: backendData.platform,
      prompt: "", // No prompt for legacy alerts
      description: backendData.description || "Legacy custom SQL alert",
      variables: [],
      destination_config,
      status:
        backendData.status === SubscriptionStatus.ACTIVE
          ? SubscriptionStatus.ACTIVE
          : SubscriptionStatus.INACTIVE,
      created_at: backendData.created_at,
      updated_at: backendData.updated_at,
      created_by: String(backendData.created_by),
      read_only: readOnly,
    };
  }

  const readOnly = backendData.read_only ?? false;

  const variables: SubscriptionV2Variable[] = [];

  // Get category from top-level field
  const category = backendData.category;

  // Extract entity with category identifier (will be looked up on frontend)
  variables.push(extractEntityVariable(backendData.entity_type, category));

  // Extract metric
  variables.push(extractMetricVariable(backendData.metric_config.metric_name));

  const params = backendData.metric_config.params || {};

  // Extract aggregation (always present, uses default if not in backend)
  variables.push(extractAggregationVariable(params));

  // For alerts, extract comparison-related variables
  const isAlertType = backendData.rule_type === SubscriptionType.ALERT;
  if (isAlertType) {
    const changeTypeVar = extractChangeTypeVariable(params);
    const changeType = changeTypeVar.value as string;

    variables.push(changeTypeVar);
    variables.push(extractComparisonVariable(params));
    variables.push(extractThresholdVariable(params));

    // Only add time_period variable for relative_value alerts
    if (changeType === "relative_value") {
      variables.push(extractDateRangeVariable(params));
    }
  }

  // Extract schedule from delivery.schedule (may be null)
  variables.push(extractScheduleVariable(backendData.delivery.schedule ?? undefined));

  // Generate prompt
  const prompt = generatePrompt(backendData.rule_type, variables, backendData.entity_type);

  // Transform destination config from delivery.notifiers
  const destination_config = extractDestinationConfig(backendData.delivery.notifiers);

  return {
    id: String(backendData.id),
    name: backendData.name,
    rule_type: backendData.rule_type,
    platform: backendData.platform,
    prompt,
    description: backendData.description,
    variables,
    destination_config,
    status:
      backendData.status === SubscriptionStatus.ACTIVE
        ? SubscriptionStatus.ACTIVE
        : SubscriptionStatus.INACTIVE,
    created_at: backendData.created_at,
    updated_at: backendData.updated_at,
    created_by: String(backendData.created_by),
    read_only: readOnly,
  };
}
